/**
 * web/src/components/speaker/SpeakerBatchDrawer.tsx
 * The speaker batch actions drawer (design: docs/design_handoff_speaker_batch).
 * One right-side Drawer that swaps IN PLACE between three views so a bulk action
 * reads as one continuous object:
 *   - SELECT + CONFIRM — see exactly who's included, adjust the set, understand
 *     the cost, commit once (the sticky footer summary IS the confirmation).
 *   - RUNNING — live progress block (done/total, ETA, two-tone bar, legend) +
 *     a thin per-speaker list; "close and keep working" hands off to the dock.
 *   - RESULT — success/partial, retry failed, and a forward handoff into the
 *     review stack. Never a dead end.
 *
 * Mounted once in AppShell (inside <BatchRunProvider>), so reopening from the
 * run dock works on any route. Reads all run state from useBatchRun; the roster
 * is the only writer of the select config. No send path — a card run opens the
 * illustration gate, a logistics run opens the logistics gate for review.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from '../ui/drawer';
import { Button } from '../shared/Button';
import { Icon, type IconName } from '../../lib/icons';
import { cn } from '../../lib/cn';
import { sessionsPreview, type BatchRun, type BatchRunAction } from '../../api/speakerBatch';
import { useBatchRun } from './BatchRunContext';

// ---------------------------------------------------------------------------
// Per-action copy + timing (design "Copy for every key moment" + §2 duration)
// ---------------------------------------------------------------------------

const CONCURRENCY = 2;

interface ActionMeta {
  label: string;
  icon: IconName;
  perItemSec: number;
  nounPlural: string;
  runVerb: string;
  cost: string;
  emptyBody: string;
  confirmCta: (n: number) => string;
  handoffCta: (done: number) => string;
}

const ACTIONS: Record<BatchRunAction, ActionMeta> = {
  cards: {
    label: 'Generate illustrations',
    icon: 'sparkles',
    perItemSec: 20,
    nounPlural: 'cards',
    runVerb: 'Generating',
    cost: 'Each card is a real image generation — roughly 20 seconds and one credit.',
    emptyBody:
      'Illustrations need a confirmed speaker with a headshot and bio on file. As assets land, speakers show up here automatically.',
    confirmCta: (n) => `Generate ${n} card${n === 1 ? '' : 's'}`,
    handoffCta: (done) => `Review ${done} card${done === 1 ? '' : 's'}`,
  },
  logistics: {
    label: 'Review logistics',
    icon: 'mail',
    perItemSec: 3,
    nounPlural: 'emails',
    runVerb: 'Opening',
    cost: 'Nothing sends yet. Drafts open in the review stack — you approve and send each one.',
    emptyBody:
      'Logistics opens 7 days before the event, once assets are in. Speakers appear here when it’s ready.',
    confirmCta: (n) => `Open ${n} for review`,
    handoffCta: (done) => `Review ${done} email${done === 1 ? '' : 's'}`,
  },
  promotion: {
    label: 'Send speaker cards',
    icon: 'mail',
    perItemSec: 3,
    nounPlural: 'cards',
    runVerb: 'Opening',
    cost: 'Nothing sends yet. Each card email opens in the review stack — you approve and send each one.',
    emptyBody:
      'Sending a card needs the finished speaker card on the monday row. Speakers appear here once their card is published.',
    confirmCta: (n) => `Open ${n} for review`,
    handoffCta: (done) => `Review ${done} email${done === 1 ? '' : 's'}`,
  },
  prep_notes: {
    label: 'Send prep notes',
    icon: 'mail',
    perItemSec: 3,
    nounPlural: 'emails',
    runVerb: 'Opening',
    cost: 'Nothing sends yet. Drafts open in the review stack — you approve and send each one.',
    emptyBody:
      'Prep notes open once the prep call is done. Speakers appear here when they’re ready.',
    confirmCta: (n) => `Open ${n} for review`,
    handoffCta: (done) => `Review ${done} email${done === 1 ? '' : 's'}`,
  },
  thank_you: {
    label: 'Send thank-yous',
    icon: 'mail',
    perItemSec: 3,
    nounPlural: 'emails',
    runVerb: 'Opening',
    cost: 'Nothing sends yet. Drafts open in the review stack — you approve and send each one.',
    emptyBody: 'Thank-yous open after the event. Speakers appear here when they’re ready.',
    confirmCta: (n) => `Open ${n} for review`,
    handoffCta: (done) => `Review ${done} email${done === 1 ? '' : 's'}`,
  },
  // The cards-board stage advances. These runs are started from the Speaker
  // Cards board (never from the roster tray) — the drawer only ever shows them
  // when the dock reopens onto an adopted run, so the meta is progress-only.
  card_finalize: {
    label: 'Approve illustrations',
    icon: 'sparkles',
    perItemSec: 25,
    nounPlural: 'illustrations',
    runVerb: 'Finalizing',
    cost: 'Each finalize paints the border and cuts the portrait out — roughly 25 seconds per speaker.',
    emptyBody: 'Started from the Speaker cards board — nothing to select here.',
    confirmCta: (n) => `Finalize ${n} illustration${n === 1 ? '' : 's'}`,
    handoffCta: (done) => `Review ${done} border${done === 1 ? '' : 's'}`,
  },
  card_build: {
    label: 'Build speaker cards',
    icon: 'sparkles',
    perItemSec: 10,
    nounPlural: 'cards',
    runVerb: 'Building',
    cost: 'Composes the speaker card from the approved portrait — nothing publishes yet.',
    emptyBody: 'Started from the Speaker cards board — nothing to select here.',
    confirmCta: (n) => `Build ${n} card${n === 1 ? '' : 's'}`,
    handoffCta: (done) => `Review ${done} card${done === 1 ? '' : 's'}`,
  },
  card_publish: {
    label: 'Publish speaker cards',
    icon: 'upload',
    perItemSec: 3,
    nounPlural: 'cards',
    runVerb: 'Publishing',
    cost: 'Publishes the finished deliverables to Drive and the monday board columns.',
    emptyBody: 'Started from the Speaker cards board — nothing to select here.',
    confirmCta: (n) => `Publish ${n} card${n === 1 ? '' : 's'}`,
    handoffCta: (done) => `${done} published`,
  },
  // Bulk redraft started from Speaker Approvals ("Regenerate all") — never
  // from the roster tray; the drawer only shows it when the dock reopens onto
  // the run, so the meta is progress-only.
  regenerate: {
    label: 'Regenerate drafts',
    icon: 'retry',
    perItemSec: 2,
    nounPlural: 'drafts',
    runVerb: 'Regenerating',
    cost: 'Rebuilds each open draft from the current templates and defaults. Nothing is sent.',
    emptyBody: 'Started from Speaker approvals — nothing to select here.',
    confirmCta: (n) => `Regenerate ${n} draft${n === 1 ? '' : 's'}`,
    handoffCta: (done) => `Review ${done} draft${done === 1 ? '' : 's'}`,
  },
};

function fmtEta(sec: number): string {
  if (sec <= 0) return 'finishing';
  if (sec < 60) return `about ${Math.ceil(sec / 10) * 10}s left`;
  return `about ${Math.round(sec / 60)} min left`;
}

function fmtDur(sec: number): string {
  return sec < 60 ? `~${sec}s` : `~${Math.round(sec / 60)} min`;
}

// ---------------------------------------------------------------------------
// Chips (design .bx-chip variants) + selection checkbox
// ---------------------------------------------------------------------------

type ChipVariant = 'default' | 'ok' | 'warn' | 'fail' | 'go' | 'run' | 'mute';

const CHIP_CLS: Record<ChipVariant, string> = {
  default: 'bg-bg-muted border-border text-text-muted',
  ok: 'bg-completed-soft border-completed-border text-completed',
  warn: 'bg-waiting-soft border-waiting-border text-waiting',
  fail: 'bg-failed-soft border-failed-border text-failed',
  go: 'bg-type-speaker-soft border-type-speaker-border text-type-speaker-text',
  run: 'bg-accent-soft border-accent-border text-accent-text',
  mute: 'bg-bg-muted border-border text-text-subtle',
};

function Chip({
  variant = 'default',
  children,
}: {
  variant?: ChipVariant;
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-[9px] py-[3px] text-[11.5px] font-semibold',
        CHIP_CLS[variant],
      )}
    >
      {children}
    </span>
  );
}

/** 2px accent ring spinner (design .bx-spin). */
function Spin({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'inline-block h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-accent-border border-t-accent',
        className,
      )}
    />
  );
}

function Check({ on, disabled }: { on: boolean; disabled?: boolean }) {
  return (
    <span
      className={cn(
        'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
        on ? 'border-type-speaker bg-type-speaker text-white' : 'border-border-strong bg-bg',
        disabled && 'opacity-40',
      )}
    >
      {on && <Icon name="check" size={11} />}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Drawer shell
// ---------------------------------------------------------------------------

export function SpeakerBatchDrawer() {
  const { drawerOpen, closeDrawer, action, run, openNonce } = useBatchRun();

  // RUN mode when a run exists for the action the drawer is showing; else SELECT.
  const showRun = run != null && action != null && run.action === action;

  return (
    <Drawer open={drawerOpen} onOpenChange={(o) => (o ? undefined : closeDrawer())}>
      <DrawerContent className="w-[min(560px,94vw)] p-0">
        {action == null ? null : showRun ? (
          <RunView run={run} action={action} />
        ) : (
          <SelectView key={openNonce} action={action} />
        )}
      </DrawerContent>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// SELECT + CONFIRM
// ---------------------------------------------------------------------------

function SelectView({ action }: { action: BatchRunAction }) {
  const { candidates, seedIds, eventId, closeDrawer, startRun, starting } = useBatchRun();
  const meta = ACTIONS[action];

  // Seed restricts the candidate pool to the roster selection, if any.
  const pool = useMemo(
    () => (seedIds ? candidates.filter((c) => seedIds.includes(c.itemId)) : candidates),
    [candidates, seedIds],
  );
  const eligible = useMemo(() => pool.filter((c) => c.eligible), [pool]);
  const skipped = useMemo(() => pool.filter((c) => !c.eligible), [pool]);

  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(eligible.map((c) => c.itemId)),
  );
  const [showSkip, setShowSkip] = useState(false);

  // Multi-session flag at selection time (logistics only) — reads the agenda once.
  const sessions = useQuery({
    queryKey: ['speakerSessionsPreview', eventId, eligible.map((c) => c.itemId).join(',')],
    queryFn: () =>
      sessionsPreview(
        eventId as string,
        eligible.map((c) => ({ itemId: c.itemId, name: c.name })),
      ),
    enabled: action === 'logistics' && eventId != null && eligible.length > 0,
    staleTime: 60_000,
  });
  const sessionsByItem = useMemo(() => {
    const m = new Map<
      string,
      { title?: string | null; day?: string | null; time?: string | null; stage?: string | null }[]
    >();
    for (const p of sessions.data ?? []) m.set(p.itemId, p.sessions);
    return m;
  }, [sessions.data]);
  const multiCount = (id: string) => sessionsByItem.get(id)?.length ?? 0;

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allOn = eligible.length > 0 && selected.size === eligible.length;

  const n = selected.size;
  const flaggedCount = eligible.filter(
    (c) => selected.has(c.itemId) && multiCount(c.itemId) > 1,
  ).length;

  const onConfirm = () => {
    const seeds = eligible
      .filter((c) => selected.has(c.itemId))
      .map((c) => ({ itemId: c.itemId, name: c.name, org: c.org }));
    // Card generation needs no step id; the gate-release actions (logistics /
    // promotion) release the step whose id matches the action name.
    void startRun(seeds, action === 'cards' ? undefined : action);
  };

  return (
    <>
      <DrawerHeader className="flex items-start gap-3 px-5 py-4">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-type-speaker-border bg-type-speaker-soft text-type-speaker-text">
          <Icon name={meta.icon} size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <DrawerTitle>{meta.label}</DrawerTitle>
          <DrawerDescription className="mt-0.5 leading-snug">
            {eligible.length
              ? `${eligible.length} speaker${eligible.length === 1 ? ' is' : 's are'} eligible${
                  seedIds ? ' in your selection' : ''
                }. Everyone is included — uncheck anyone you want to leave out.`
              : 'Nothing is eligible right now.'}
          </DrawerDescription>
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={closeDrawer}
          className="rounded p-1 text-text-subtle hover:bg-bg-muted"
        >
          <Icon name="x" size={15} />
        </button>
      </DrawerHeader>

      <div className="flex-1 overflow-y-auto px-5 py-4">
        {eligible.length === 0 ? (
          <div className="mx-auto flex max-w-[340px] flex-col items-center gap-2 py-10 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-bg-muted text-text-subtle">
              <Icon name={meta.icon} size={17} />
            </div>
            <div className="text-sm font-semibold">No one is eligible yet</div>
            <div className="text-[12.5px] text-text-subtle">{meta.emptyBody}</div>
          </div>
        ) : (
          <>
            <SectionLine label={`Included · ${n} of ${eligible.length}`}>
              <button
                type="button"
                className="text-[11.5px] font-semibold text-accent"
                onClick={() =>
                  setSelected(allOn ? new Set() : new Set(eligible.map((c) => c.itemId)))
                }
              >
                {allOn ? 'Clear all' : 'Select all'}
              </button>
            </SectionLine>

            <div className="overflow-hidden rounded-lg border border-border">
              {eligible.map((c, i) => {
                const on = selected.has(c.itemId);
                const multi = multiCount(c.itemId) > 1;
                return (
                  <button
                    type="button"
                    key={c.itemId}
                    onClick={() => toggle(c.itemId)}
                    className={cn(
                      'grid w-full grid-cols-[16px_1fr_auto] items-start gap-[11px] px-3 py-2.5 text-left',
                      i > 0 && 'border-t border-border',
                      !on ? 'bg-bg-soft' : multi ? 'bg-waiting-soft' : 'bg-bg',
                    )}
                  >
                    <span className="mt-0.5">
                      <Check on={on} />
                    </span>
                    <span className="min-w-0">
                      <span
                        className={cn(
                          'block truncate text-[13px] font-semibold',
                          !on && 'text-text-subtle',
                        )}
                      >
                        {c.name}
                      </span>
                      <span className="block truncate text-[11.5px] text-text-subtle">
                        {c.org || '—'}
                      </span>
                      {multi && on && (
                        <span className="mt-1.5 block rounded-md border border-waiting-border bg-bg p-2">
                          <span className="flex items-center gap-1.5 text-[11.5px] font-semibold text-waiting">
                            <Icon name="alert" size={12} /> {multiCount(c.itemId)} sessions on the
                            agenda — the draft covers only the first.
                          </span>
                          <span className="mt-1 block space-y-0.5">
                            {(sessionsByItem.get(c.itemId) ?? []).map((s, si) => (
                              <span key={si} className="flex justify-between gap-2 text-[11px]">
                                <span className="truncate font-medium text-text">
                                  {s.title || 'Session'}
                                </span>
                                <span className="shrink-0 tabular text-text-subtle">
                                  {[s.day, s.time].filter(Boolean).join(' · ')}
                                </span>
                              </span>
                            ))}
                          </span>
                        </span>
                      )}
                    </span>
                    <span className="pt-0.5">
                      {multi && (
                        <Chip variant="warn">
                          <Icon name="alert" size={11} /> {multiCount(c.itemId)} sessions
                        </Chip>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>

            {flaggedCount > 0 && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-waiting-border bg-waiting-soft px-3 py-2.5 text-[12px] text-waiting">
                <Icon name="alert" size={14} className="mt-0.5 shrink-0" />
                <span>
                  <b>
                    {flaggedCount} speaker{flaggedCount === 1 ? '' : 's'} to check at review.
                  </b>{' '}
                  Their drafts show one session only. They stay in the batch and are flagged again
                  in the review stack — you can add the missing sessions before sending.
                </span>
              </div>
            )}

            {skipped.length > 0 && (
              <div className="mt-4">
                <SectionLine label={`Not eligible · ${skipped.length}`}>
                  <button
                    type="button"
                    className="text-[11.5px] font-semibold text-accent"
                    onClick={() => setShowSkip((v) => !v)}
                  >
                    {showSkip ? 'Hide' : 'Show reasons'}
                  </button>
                </SectionLine>
                {showSkip && (
                  <div className="overflow-hidden rounded-lg border border-border">
                    {skipped.map((c, i) => (
                      <div
                        key={c.itemId}
                        className={cn(
                          'grid grid-cols-[16px_1fr] items-start gap-[11px] bg-bg-soft px-3 py-2.5',
                          i > 0 && 'border-t border-border',
                        )}
                      >
                        <span className="mt-0.5">
                          <Check on={false} disabled />
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate text-[13px] font-semibold text-text-subtle">
                            {c.name}
                          </span>
                          <span className="block text-[11.5px] text-text-subtle">
                            {c.skipReason ?? 'Not eligible'}
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {eligible.length > 0 && (
        <div className="flex flex-shrink-0 items-center gap-3 border-t border-border bg-bg-soft px-5 py-3">
          <div className="min-w-0 flex-1">
            <div className="text-[12.5px] tabular">
              <b>{n}</b> of {eligible.length} included · <b>{fmtDur(n * meta.perItemSec)}</b>
            </div>
            <div className="text-[11.5px] text-text-subtle">{meta.cost}</div>
          </div>
          <Button size="sm" variant="default" onClick={closeDrawer}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" disabled={n === 0 || starting} onClick={onConfirm}>
            <Icon name={action === 'cards' ? 'play' : 'mail'} size={12} />
            {starting ? 'Starting…' : meta.confirmCta(n)}
          </Button>
        </div>
      )}
    </>
  );
}

function SectionLine({ label, children }: { label: string; children?: React.ReactNode }) {
  return (
    <div className="mb-2 flex items-center">
      <span className="text-[10.5px] font-bold uppercase tracking-[0.07em] text-text-subtle">
        {label}
      </span>
      <span className="flex-1" />
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RUNNING + RESULT
// ---------------------------------------------------------------------------

function RunView({ run, action }: { run: BatchRun; action: BatchRunAction }) {
  const { closeDrawer, retry, retrying, dismiss } = useBatchRun();
  const navigate = useNavigate();
  const meta = ACTIONS[action];

  const total = run.items.length;
  const done = run.items.filter((i) => i.status === 'done').length;
  const failed = run.items.filter((i) => i.status === 'failed').length;
  const running = run.items.filter((i) => i.status === 'running').length;
  const queued = run.items.filter((i) => i.status === 'queued').length;
  const left = total - done - failed;
  const eta = Math.ceil(left / CONCURRENCY) * meta.perItemSec;
  const finished = run.state === 'done';

  const onHandoff = () => {
    dismiss();
    // Card runs land on the Speaker Cards pipeline board (2026-08-20) — the
    // freshly-opened gates surface there as "Choose illustration" tiles.
    if (action === 'cards') {
      navigate('/speakers/cards');
      return;
    }
    const gates = run.items
      .filter((i) => i.status === 'done' && i.workflowId != null && i.stepIndex != null)
      .map((i) => ({ workflowId: i.workflowId as string, stepIndex: i.stepIndex as number }));
    // Email actions land on Speaker Approvals, which opens its review queue on
    // exactly the gates this run opened (autoQueue).
    navigate('/speakers/approvals', { state: { gates, autoQueue: true } });
  };

  return (
    <>
      <DrawerHeader className="flex items-start gap-3 px-5 py-4">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-type-speaker-border bg-type-speaker-soft text-type-speaker-text">
          <Icon name={meta.icon} size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <DrawerTitle>
            {finished
              ? failed
                ? `${done} of ${total} ${meta.nounPlural} ready`
                : `${meta.label} — done`
              : `${meta.runVerb} ${total} ${meta.nounPlural}`}
          </DrawerTitle>
          <DrawerDescription className="mt-0.5 leading-snug">
            {finished
              ? failed
                ? `${failed} failed and can be retried. The rest are ready for review.`
                : 'All done. Nothing has been sent yet.'
              : 'You can close this and keep working — it keeps running in the background.'}
          </DrawerDescription>
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={closeDrawer}
          className="rounded p-1 text-text-subtle hover:bg-bg-muted"
        >
          <Icon name="x" size={15} />
        </button>
      </DrawerHeader>

      <div className="flex-1 overflow-y-auto px-5 py-4">
        {finished ? (
          <div className="flex flex-col items-center gap-2 px-2 pb-4 pt-2 text-center">
            <div
              className={cn(
                'flex h-11 w-11 items-center justify-center rounded-full',
                failed ? 'bg-waiting-soft text-waiting' : 'bg-completed-soft text-completed',
              )}
            >
              <Icon name={failed ? 'alert' : 'check'} size={19} />
            </div>
            <div className="text-base font-semibold">
              {failed
                ? `${done} ready · ${failed} failed`
                : `${done} ${meta.nounPlural} ready to review`}
            </div>
            <div className="text-[12.5px] text-text-subtle">
              {failed
                ? `${failed} ${failed === 1 ? 'generation' : 'generations'} didn't complete. Retry them now, or move on and come back — nothing is lost.`
                : action === 'cards'
                  ? 'Review the cards one at a time. Approve keeps the card and moves the speaker forward.'
                  : 'Approve and send each email in the review stack.'}
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-bg-soft px-4 py-3.5">
            <div className="flex items-center justify-between">
              <div className="text-[22px] font-bold tabular tracking-[-0.02em]">
                {done + failed}
                <span className="text-[13px] font-semibold text-text-subtle"> / {total}</span>
              </div>
              <div className="flex items-center gap-1.5 whitespace-nowrap text-[13px] text-text-subtle">
                <Spin /> {fmtEta(eta)}
              </div>
            </div>
            <div className="mt-2.5 flex h-1.5 overflow-hidden rounded-full bg-bg-muted">
              <span
                className="bg-type-speaker transition-[width] duration-300"
                style={{ width: `${(done / total) * 100}%` }}
              />
              <span
                className="bg-failed transition-[width] duration-300"
                style={{ width: `${(failed / total) * 100}%` }}
              />
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11.5px] text-text-subtle">
              <Legend dot="bg-type-speaker" label={`${done} done`} />
              <Legend dot="bg-accent" label={`${running} in flight`} />
              <Legend dot="bg-border-strong" label={`${queued} queued`} />
              {failed > 0 && <Legend dot="bg-failed" label={`${failed} failed`} />}
            </div>
          </div>
        )}

        <div className="mb-2 mt-4 text-[10.5px] font-bold uppercase tracking-[0.07em] text-text-subtle">
          Speakers
        </div>
        <div className="overflow-hidden rounded-lg border border-border">
          {run.items.map((it, i) => (
            <div
              key={it.itemId}
              className={cn(
                'grid items-center gap-[11px] px-3 py-2.5',
                action === 'cards' ? 'grid-cols-[34px_1fr_auto]' : 'grid-cols-[16px_1fr_auto]',
                i > 0 && 'border-t border-border',
              )}
            >
              {action === 'cards' ? (
                <span
                  className={cn(
                    'flex h-11 w-[34px] items-center justify-center overflow-hidden rounded',
                    it.status === 'done'
                      ? 'bg-gradient-to-br from-[#e9e2f0] to-[#d8cfe4]'
                      : it.status === 'running'
                        ? 'animate-pulse bg-bg-muted'
                        : 'bg-bg-muted',
                  )}
                >
                  {it.status === 'failed' && (
                    <Icon name="alert" size={13} className="text-failed" />
                  )}
                </span>
              ) : (
                <Check on={it.status === 'done'} disabled />
              )}
              <div className="min-w-0">
                <div className="truncate text-[13px] font-semibold">{it.name}</div>
                <div className="truncate text-[11.5px] text-text-subtle">
                  {it.status === 'failed' ? it.reason : it.org}
                </div>
              </div>
              <div>
                {it.status === 'queued' && <Chip variant="mute">Queued</Chip>}
                {it.status === 'running' && (
                  <Chip variant="run">
                    <Spin /> {meta.runVerb}…
                  </Chip>
                )}
                {it.status === 'done' && (
                  <Chip variant="go">
                    <Icon name="check" size={11} /> Ready
                  </Chip>
                )}
                {it.status === 'failed' && (
                  <Chip variant="fail">
                    <Icon name="alert" size={11} /> Failed
                  </Chip>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-shrink-0 items-center gap-3 border-t border-border bg-bg-soft px-5 py-3">
        <div className="min-w-0 flex-1">
          <div className="text-[12.5px] tabular">
            {finished ? (
              <>
                <b>{done}</b> ready · <b>{failed}</b> failed
              </>
            ) : (
              <>
                <b>{done + failed}</b> of {total} · {fmtEta(eta)}
              </>
            )}
          </div>
          <div className="text-[11.5px] text-text-subtle">
            {finished ? 'Nothing has been sent.' : 'Runs in the background.'}
          </div>
        </div>
        {finished && failed > 0 && (
          <Button size="sm" variant="default" onClick={() => void retry()} disabled={retrying}>
            <Icon name="retry" size={12} /> {retrying ? 'Retrying…' : `Retry ${failed}`}
          </Button>
        )}
        {!finished && (
          <Button size="sm" variant="default" onClick={closeDrawer}>
            Close
          </Button>
        )}
        <Button size="sm" variant="primary" disabled={done === 0} onClick={onHandoff}>
          {meta.handoffCta(done)} <Icon name="chevron-right" size={12} />
        </Button>
      </div>
    </>
  );
}

function Legend({ dot, label }: { dot: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn('h-[7px] w-[7px] rounded-full', dot)} /> {label}
    </span>
  );
}
